import { useState } from 'react';
import { ChevronLeftIcon, ChevronDownIcon, EyeOffIcon } from '../components/Icons';
import { sendRegistrationOtp, register } from '../db';

const states = [
  'Abia', 'Adamawa', 'Akwa Ibom', 'Anambra', 'Bauchi', 'Bayelsa', 'Benue',
  'Borno', 'Cross River', 'Delta', 'Ebonyi', 'Edo', 'Ekiti', 'Enugu', 'FCT',
  'Gombe', 'Imo', 'Jigawa', 'Kaduna', 'Kano', 'Katsina', 'Kebbi', 'Kogi',
  'Kwara', 'Lagos', 'Nasarawa', 'Niger', 'Ogun', 'Ondo', 'Osun', 'Oyo',
  'Plateau', 'Rivers', 'Sokoto', 'Taraba', 'Yobe', 'Zamfara',
];

function BrandPanel() {
  return (
    <div className="hidden lg:flex flex-col justify-between bg-[#F47B20] p-12 text-white">
      {/* Logo */}
      <div className="flex items-center gap-3">
        <img src="/logo.png" alt="Manna Bridal Logo" className="h-12 object-contain" />
      </div>

      {/* Central content */}
      <div className="space-y-6">
        <div>
          <p className="text-white/70 text-sm font-medium uppercase tracking-wider mb-3">Premium Bridal Selection</p>
          <h2 className="text-4xl font-black leading-tight">
            Find your dream dress,<br />effortlessly.
          </h2>
        </div>
        <div className="space-y-3">
          {[
            'Find the perfect dress',
            'No hidden charges ever',
            'Thousands of verified products',
            'Fast delivery nationwide',
          ].map(item => (
            <div key={item} className="flex items-center gap-3">
              <div className="w-5 h-5 bg-white/25 rounded-full flex items-center justify-center flex-shrink-0">
                <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <span className="text-white/90 text-sm">{item}</span>
            </div>
          ))}
        </div>
      </div>


    </div>
  );
}

export default function SignUpPage({ onNavigate, onUserLogin }) {
  const [showPass, setShowPass] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', state: '', password: '', referral: '', otp: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState(1);

  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.password) {
      setError('Please fill in name, email, and password.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      await sendRegistrationOtp(form.name, form.email, form.password);
      setStep(2);
    } catch (err) {
      if (err.response?.data?.errors) {
        const firstError = Object.values(err.response.data.errors)[0][0];
        if (firstError === 'already used') {
          setError('This email is already registered. Please try a different one.');
        } else {
          setError(firstError);
        }
      } else if (err.response?.data?.message && err.response.data.message !== 'The given data was invalid.') {
        if (err.response.data.message === 'already used') {
          setError('This email is already registered. Please try a different one.');
        } else {
          setError(err.response.data.message);
        }
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.otp || form.otp.length !== 6) {
      setError('Please enter the 6-digit OTP.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const res = await register(form.email, form.otp);
      localStorage.setItem('auth_token', res.access_token);
      localStorage.setItem('user', JSON.stringify(res.user));
      if (onUserLogin) onUserLogin(res.user);
      setStep(3); // show welcome screen
    } catch (err) {
      if (err.response?.data?.message && err.response.data.message !== 'The given data was invalid.') {
        if (err.response.data.message === 'already used') {
          setError('This email is already registered. Please try a different one.');
        } else {
          setError(err.response.data.message);
        }
      } else if (err.response?.status === 422 || err.response?.status === 400) {
        setError('That code is invalid or has expired. Please try again.');
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleStartShopping = () => {
    onNavigate('shop');
  };


  return (
    <div className="min-h-screen bg-[#F2F4F7] lg:grid lg:grid-cols-[45%_55%]">
      <BrandPanel />

      {/* Form Side */}
      <div className="flex flex-col min-h-screen lg:min-h-0">
        {/* Mobile/Tablet header */}
        <div className="lg:hidden flex items-center px-4 pt-6 pb-2">
          <button onClick={() => onNavigate('shop')} className="p-2 -ml-2 text-[#F47B20]">
            <ChevronLeftIcon className="w-6 h-6" />
          </button>
        </div>

        <div className="flex-1 flex flex-col lg:justify-center px-4 md:px-12 lg:px-16 pb-8 lg:py-12 overflow-y-auto">
          {/* Desktop back */}
          <button onClick={() => onNavigate('shop')} className="hidden lg:flex items-center gap-1 text-gray-400 hover:text-gray-600 mb-8 text-sm">
            <ChevronLeftIcon className="w-4 h-4" /> Back to shop
          </button>

          {step !== 3 && (
            <div className="mb-6">
              <h1 className="text-2xl md:text-3xl font-black text-[#1A1A2E]">Create an account</h1>
              <p className="text-gray-500 text-sm mt-1.5">Welcome to Manna Bridal! Create your account in seconds to start shopping for the things you love.</p>
            </div>
          )}

          {/* Form card */}
          {step === 1 ? (
            <div className="bg-white rounded-2xl p-5 md:p-6 shadow-sm mb-4">
              <div className="flex items-center gap-1 mb-4 text-sm text-gray-500">
                Have an Account?{' '}
                <button onClick={() => onNavigate('login')} className="text-[#F47B20] font-semibold ml-1">Login</button>
              </div>

              <div className="space-y-3">
                <input name="name" value={form.name} onChange={handleChange}
                  placeholder="Full Name"
                  className="w-full px-4 py-3.5 rounded-xl border border-gray-200 text-sm text-[#1A1A2E] placeholder-gray-400 outline-none focus:border-[#F47B20] focus:ring-2 focus:ring-[#F47B20]/10 transition-all"
                />
                <input name="email" value={form.email} onChange={handleChange}
                  placeholder="Email Address" type="email"
                  className="w-full px-4 py-3.5 rounded-xl border border-gray-200 text-sm text-[#1A1A2E] placeholder-gray-400 outline-none focus:border-[#F47B20] focus:ring-2 focus:ring-[#F47B20]/10 transition-all"
                />
                <input name="phone" value={form.phone} onChange={handleChange}
                  onFocus={(e) => { if (!e.target.value) setForm(f => ({ ...f, phone: '+234 ' })); }}
                  onBlur={(e) => { if (e.target.value.trim() === '+234') setForm(f => ({ ...f, phone: '' })); }}
                  placeholder="+234 80..." type="tel"
                  className="w-full px-4 py-3.5 rounded-xl border border-gray-200 text-sm text-[#1A1A2E] placeholder-gray-400 outline-none focus:border-[#F47B20] focus:ring-2 focus:ring-[#F47B20]/10 transition-all"
                />
                <div className="relative">
                  <select name="state" value={form.state} onChange={handleChange}
                    className="w-full px-4 py-3.5 rounded-xl border border-gray-200 text-sm outline-none focus:border-[#F47B20] focus:ring-2 focus:ring-[#F47B20]/10 transition-all appearance-none bg-white text-gray-400"
                  >
                    <option value="" disabled>Select State</option>
                    {states.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <ChevronDownIcon className="w-5 h-5 text-gray-400 absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none" />
                </div>
                <div className="relative">
                  <input name="password" value={form.password} onChange={handleChange}
                    placeholder="Password" type={showPass ? 'text' : 'password'}
                    className="w-full px-4 py-3.5 rounded-xl border border-gray-200 text-sm text-[#1A1A2E] placeholder-gray-400 outline-none focus:border-[#F47B20] focus:ring-2 focus:ring-[#F47B20]/10 transition-all pr-12"
                  />
                  <button type="button" onClick={() => setShowPass(v => !v)} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    <EyeOffIcon className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          ) : step === 2 ? (
            <div className="bg-white rounded-2xl p-5 md:p-6 shadow-sm mb-4">
              <div className="flex flex-col items-center justify-center py-4">
                <div className="w-16 h-16 bg-[#F47B20]/10 rounded-full flex items-center justify-center mb-4">
                  <svg className="w-8 h-8 text-[#F47B20]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-[#1A1A2E] mb-2">Verify your email</h3>
                <p className="text-sm text-gray-500 text-center mb-4">
                  We've sent a 6-digit code to <br/><span className="font-medium text-[#1A1A2E]">{form.email}</span>
                </p>

                <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2.5 mb-5 w-full">
                  <svg className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-xs text-amber-700">Can't find the email? Check your <span className="font-bold">spam or junk folder</span>.</p>
                </div>

                <input 
                  name="otp" 
                  value={form.otp} 
                  onChange={handleChange}
                  placeholder="Enter 6-digit OTP" 
                  type="text"
                  maxLength={6}
                  className="w-full text-center tracking-widest text-xl font-mono px-4 py-4 rounded-xl border border-gray-200 text-[#1A1A2E] placeholder-gray-300 outline-none focus:border-[#F47B20] focus:ring-2 focus:ring-[#F47B20]/10 transition-all mb-4"
                />

                <button onClick={() => setStep(1)} className="text-sm text-gray-400 hover:text-[#F47B20]">
                  Change email address
                </button>
              </div>
            </div>
          ) : (
            /* ── Step 3: Welcome screen ── */
            <div className="flex flex-col items-center text-center py-4">
              {/* Animated checkmark */}
              <div className="relative mb-6">
                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-[#F47B20] to-[#e06010] flex items-center justify-center shadow-lg shadow-[#F47B20]/30">
                  <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div className="absolute -top-1 -right-1 w-6 h-6 bg-green-400 rounded-full flex items-center justify-center">
                  <svg className="w-3.5 h-3.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>

              <h2 className="text-2xl md:text-3xl font-black text-[#1A1A2E] mb-2">
                Welcome to Manna Bridal!
              </h2>
              <p className="text-gray-500 text-sm mb-1">
                Hi <span className="font-semibold text-[#1A1A2E]">{form.name}</span>, your account has been created successfully.
              </p>
              <p className="text-gray-400 text-sm mb-8">
                You're all set to discover our exclusive bridal collection.
              </p>

              {/* Feature highlights */}
              <div className="grid grid-cols-3 gap-3 w-full mb-8">
                <div className="bg-white rounded-2xl p-3 shadow-sm flex flex-col items-center gap-2">
                  <svg className="w-6 h-6 text-[#F47B20]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M16 11c0 2.21-1.79 4-4 4s-4-1.79-4-4V7a4 4 0 018 0v4zM4 20c0-3.31 3.58-6 8-6s8 2.69 8 6" />
                  </svg>
                  <span className="text-xs font-medium text-[#1A1A2E]">Bridal Wear</span>
                </div>
                <div className="bg-white rounded-2xl p-3 shadow-sm flex flex-col items-center gap-2">
                  <svg className="w-6 h-6 text-[#F47B20]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                  </svg>
                  <span className="text-xs font-medium text-[#1A1A2E]">Fast Delivery</span>
                </div>
                <div className="bg-white rounded-2xl p-3 shadow-sm flex flex-col items-center gap-2">
                  <svg className="w-6 h-6 text-[#F47B20]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                  </svg>
                  <span className="text-xs font-medium text-[#1A1A2E]">Exclusive Deals</span>
                </div>
              </div>

              <button
                onClick={handleStartShopping}
                className="w-full bg-[#F47B20] hover:bg-[#D96A10] active:scale-[0.98] text-white font-bold py-4 rounded-2xl text-base transition-all shadow-lg shadow-[#F47B20]/25"
              >
                Start Shopping
              </button>

              <p className="text-xs text-gray-400 mt-4">
                A welcome email has been sent to <span className="font-medium">{form.email}</span>
              </p>
            </div>
          )}

          {/* T&C + Button — hidden on welcome step */}
          {step !== 3 && (
            <>
              <div className="bg-white rounded-2xl p-4 mb-3 shadow-sm">
                <p className="text-xs text-gray-600 text-center leading-relaxed">
                  By clicking Sign Up, you agree to our{' '}
                  <span className="text-[#F47B20] underline cursor-pointer">Terms and Conditions</span>
                  {' '}and{' '}
                  <span className="text-[#F47B20] underline cursor-pointer">Privacy Policy.</span>
                  {' '}You may receive email/sms notifications from us.
                </p>
              </div>

              {error && <p className="text-red-500 text-sm text-center mb-4">{error}</p>}

              <button onClick={step === 1 ? handleSendOtp : handleSubmit} disabled={loading} className="w-full bg-[#F47B20] hover:bg-[#D96A10] text-white font-bold py-4 rounded-2xl text-base transition-colors shadow-sm disabled:opacity-70">
                {loading ? 'Processing...' : (step === 1 ? 'Continue' : 'Verify & Sign Up')}
              </button>

              <p className="text-center text-sm text-gray-500 mt-4">
                Already have an Account?{' '}
                <button onClick={() => onNavigate('login')} className="text-[#F47B20] font-semibold">Log In</button>
              </p>
            </>
          )}

        </div>
      </div>
    </div>
  );
}
